/**
 * 캐릭터 시트 → 뷰 세 장.
 *
 * 시트는 한 장에 정면·측면·후면이 가로로 놓인 1536×1024 그림이다. 여기서 할 일은
 *   1. 세 패널의 경계를 찾고 (배경 열의 세로 투영 프로파일에서 빈 골짜기 찾기),
 *   2. 패널마다 실루엣을 뽑고,
 *   3. 셋을 **같은 높이·같은 바닥선**으로 정규화하는 것이다.
 *
 * 3번이 핵심이다. 생성 모델은 "같은 크기"라고 해도 몇 % 씩 어긋나게 그린다.
 * 비주얼 헐은 세 실루엣을 교차시키므로, 높이가 3% 만 달라도 머리 꼭대기가 잘려 나간다.
 * 각 뷰의 바운딩 박스 높이를 1로 맞추고 바닥을 0에 붙이면 그 문제가 사라진다.
 */

import type { Field, Raster } from '../types.ts';
import { gaussianBlur, luminanceField, makeField, makeRaster, mapField, normalizeField } from '../imaging/raster.ts';
import { extractSilhouette } from '../imaging/silhouette.ts';

export type ViewName = 'front' | 'side' | 'back';

export interface SheetPanel {
  view: ViewName;
  /** 시트 안에서의 픽셀 사각형 */
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** 패널만 잘라낸 래스터 */
  raster: Raster;
  /** 패널 좌표계의 실루엣(페더링됨) */
  mask: Field;
  /** 실루엣의 바운딩 박스(패널 좌표) */
  bbox: { x0: number; y0: number; x1: number; y1: number };
}

export interface SheetSplit {
  panels: [SheetPanel, SheetPanel, SheetPanel];
  /** 시트 전체 마스크 — PBR 맵 추출과 텍스처 알파에 쓴다 */
  sheetMask: Field;
  /** 패널 경계를 프로파일에서 찾았는지, 3등분으로 때웠는지 */
  detected: boolean;
}

function crop(src: Raster, x0: number, y0: number, x1: number, y1: number): Raster {
  const w = x1 - x0;
  const h = y1 - y0;
  const out = makeRaster(w, h);
  for (let y = 0; y < h; y++) {
    const s = ((y0 + y) * src.width + x0) * 4;
    out.data.set(src.data.subarray(s, s + w * 4), y * w * 4);
  }
  return out;
}

function bboxOf(mask: Field) {
  const { width: w, height: h } = mask;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mask.data[y * w + x]! < 0.5) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < 0) return { x0: 0, y0: 0, x1: w - 1, y1: h - 1 };
  return { x0, y0, x1, y1 };
}

/**
 * 열마다 "피사체 픽셀이 몇 개인가"를 세고, 비어 있는 골짜기로 패널을 나눈다.
 * 골짜기가 정확히 둘이 아니면(뷰가 붙었거나 넷이거나) 3등분으로 물러선다.
 */
function findPanelBounds(mask: Field): { bounds: [number, number][]; detected: boolean } {
  const { width: w, height: h } = mask;
  const profile = new Float32Array(w);
  for (let x = 0; x < w; x++) {
    let sum = 0;
    for (let y = 0; y < h; y++) sum += mask.data[y * w + x]! >= 0.5 ? 1 : 0;
    profile[x] = sum / h;
  }

  // 점유 구간(run) 찾기. 아주 얇은 티끌은 무시한다.
  const threshold = 0.004;
  const runs: [number, number][] = [];
  let start = -1;
  for (let x = 0; x <= w; x++) {
    const on = x < w && profile[x]! > threshold;
    if (on && start < 0) start = x;
    if (!on && start >= 0) {
      if (x - start > w * 0.04) runs.push([start, x]);
      start = -1;
    }
  }

  if (runs.length === 3) {
    // 각 패널은 이웃 run 과의 중간까지 넓게 잡는다 — 여백까지 포함해야 페더링이 안 잘린다.
    const bounds: [number, number][] = runs.map((r, i) => {
      const left = i === 0 ? 0 : Math.floor((runs[i - 1]![1] + r[0]) / 2);
      const right = i === 2 ? w : Math.floor((r[1] + runs[i + 1]![0]) / 2);
      return [left, right];
    });
    return { bounds, detected: true };
  }

  const third = Math.floor(w / 3);
  return { bounds: [[0, third], [third, 2 * third], [2 * third, w]], detected: false };
}

export function splitSheet(sheet: Raster): SheetSplit {
  const whole = extractSilhouette(sheet, { keepLargest: false });
  const { bounds, detected } = findPanelBounds(whole.mask);
  const views: ViewName[] = ['front', 'side', 'back'];

  const panels = bounds.map(([x0, x1], i) => {
    const raster = crop(sheet, x0, 0, x1, sheet.height);
    // 패널 단위로 다시 뽑는다 — 전체 마스크를 자르면 이웃 패널의 페더링이 새어 들어온다.
    const sil = extractSilhouette(raster);
    const mask: Field = sil.source === 'full' ? sliceMask(whole.mask, x0, x1) : sil.mask;
    return {
      view: views[i]!,
      x0, y0: 0, x1, y1: sheet.height,
      raster,
      mask,
      bbox: bboxOf(mask),
    } satisfies SheetPanel;
  }) as [SheetPanel, SheetPanel, SheetPanel];

  return { panels, sheetMask: whole.mask, detected };
}

function sliceMask(mask: Field, x0: number, x1: number): Field {
  const w = x1 - x0;
  const out = makeField(w, mask.height);
  for (let y = 0; y < mask.height; y++) {
    out.data.set(mask.data.subarray(y * mask.width + x0, y * mask.width + x1), y * w);
  }
  return out;
}

/**
 * 뷰 실루엣을 헐 좌표계로 샘플링하는 함수.
 * 헐 좌표: 가로 s∈[0,1] (실루엣 bbox 폭을 1로), 세로 t∈[0,1] (bbox 높이를 1로, 0=바닥).
 */
export function panelSampler(panel: SheetPanel) {
  const { mask, bbox } = panel;
  const bw = Math.max(1, bbox.x1 - bbox.x0);
  const bh = Math.max(1, bbox.y1 - bbox.y0);
  return {
    /** bbox 가로세로비 (폭/높이) */
    aspect: bw / bh,
    /** s: 0..1 가로, t: 0..1 세로(0=바닥). 범위 밖은 0. */
    sample(s: number, t: number): number {
      if (s < 0 || s > 1 || t < 0 || t > 1) return 0;
      const x = bbox.x0 + s * bw;
      const y = bbox.y1 - t * bh;
      const xi = Math.round(x);
      const yi = Math.round(y);
      if (xi < 0 || yi < 0 || xi >= mask.width || yi >= mask.height) return 0;
      return mask.data[yi * mask.width + xi]!;
    },
    /** 헐 좌표 → 시트 UV (0..1, v 는 위가 1). 텍스처 투영에 쓴다. */
    toSheetUv(s: number, t: number, sheetW: number, sheetH: number): [number, number] {
      const x = panel.x0 + bbox.x0 + s * bw;
      const y = panel.y0 + bbox.y1 - t * bh;
      return [x / sheetW, 1 - y / sheetH];
    },
  };
}

/**
 * 뷰의 요철 = 휘도에서 저주파 조명을 뺀 것. 0.5 가 평균, 밝으면 튀어나오고 어두우면 들어간다.
 * 마스크 밖은 0.5(변위 없음). 헐 변위와 노멀맵이 같은 신호를 쓰므로 둘이 서로 맞는다.
 */
export function reliefSampler(panel: SheetPanel) {
  const { raster, mask, bbox } = panel;
  const luma = luminanceField(raster);
  const base = gaussianBlur(luma, Math.max(4, Math.max(raster.width, raster.height) * 0.04));
  const fine = gaussianBlur(luma, 1.2);
  const highpass = normalizeField(mapField(fine, (v, i) => v - base.data[i]!), 0.03, 0.97, mask);
  const relief = mapField(highpass, (v, i) => (mask.data[i]! >= 0.5 ? v : 0.5));
  const bw = Math.max(1, bbox.x1 - bbox.x0);
  const bh = Math.max(1, bbox.y1 - bbox.y0);
  return {
    sample(s: number, t: number): number {
      if (s < 0 || s > 1 || t < 0 || t > 1) return 0.5;
      const xi = Math.round(bbox.x0 + s * bw);
      const yi = Math.round(bbox.y1 - t * bh);
      if (xi < 0 || yi < 0 || xi >= relief.width || yi >= relief.height) return 0.5;
      return relief.data[yi * relief.width + xi]!;
    },
  };
}
